"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { BrandedHeader } from "@/components/branded";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { cn } from "@/utilities/core/cn";
import { buildLocalePath } from "@/lib/locale";
import type { LocaleCode } from "@/lib/labels/types";
import type { NationsLeagueSection } from "../routing";
import { hubUrl } from "../routing";
interface NationsLeagueBrandedHeaderProps {
  name: string;
  logoUrl?: string;
  colors?: {
    gradientFrom: string;
    gradientTo: string;
    textColor: string;
  };
  backgroundImageUrl?: string;
  breadcrumbs?: React.ReactNode;
  /** Active tier query param, forwarded on nav clicks so tab state survives. */
  tierSlug?: string;
  /** Undefined on the overview page; set on sub-pages so the right tab activates. */
  activeSection?: NationsLeagueSection;
  locale: LocaleCode;
  isMultiLang: boolean;
}

/**
 * Hub-scoped clone of `CompetitionBrandedHeader`. Same `BrandedHeader` visual +
 * same sticky link-based tab bar, but every link points at a Nations League
 * hub URL (`/football/competitions/nationsleague[/section]`) so:
 *   - the Overview tab activates on the hub landing
 *   - the News / Videos / Standings / Matches / Teams tabs activate on their
 *     respective sub-pages
 *   - the active `?tier=…` query is preserved across tab clicks
 */
export function NationsLeagueBrandedHeader(t0) {
  const $ = _c(42);
  const {
    name,
    logoUrl,
    colors,
    backgroundImageUrl,
    breadcrumbs,
    tierSlug,
    activeSection,
    locale,
    isMultiLang
  } = t0;
  const labels = useLabels();
  let t1;
  if ($[0] !== isMultiLang || $[1] !== locale || $[2] !== tierSlug) {
    t1 = section => {
      const path = hubUrl(section);
      const localized = buildLocalePath(path, locale, isMultiLang);
      return tierSlug ? `${localized}?tier=${tierSlug}` : localized;
    };
    $[0] = isMultiLang;
    $[1] = locale;
    $[2] = tierSlug;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const link = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[4] !== activeSection || $[5] !== backgroundImageUrl || $[6] !== breadcrumbs || $[7] !== colors || $[8] !== labels.competitions || $[9] !== labels.matches.title || $[10] !== labels.teamPage.tabs.news || $[11] !== labels.teamPage.tabs.overview || $[12] !== labels.teams.title || $[13] !== link || $[14] !== logoUrl || $[15] !== name) {
    const navLinks = [{
      section: undefined,
      label: labels.teamPage.tabs.overview
    }, {
      section: "news",
      label: labels.teamPage.tabs.news
    }, {
      section: "videos",
      label: (labels.competitions as {
        videos?: string;
      }).videos ?? "Videos"
    }, {
      section: "standings",
      label: labels.competitions.standings
    }, {
      section: "matches",
      label: labels.matches.title
    }, {
      section: "teams",
      label: labels.teams.title
    }];
    let t7;
    if ($[21] !== logoUrl) {
      t7 = <TeamLogo src={logoUrl} kind="competition" size="xl2" />;
      $[21] = logoUrl;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    if ($[23] !== backgroundImageUrl || $[24] !== breadcrumbs || $[25] !== colors || $[26] !== logoUrl || $[27] !== name || $[28] !== t7) {
      t6 = <BrandedHeader titleAs="h1" title={name} imageUrl={logoUrl} image={t7} colors={colors} backgroundImageUrl={backgroundImageUrl} breadcrumbs={breadcrumbs} />;
      $[23] = backgroundImageUrl;
      $[24] = breadcrumbs;
      $[25] = colors;
      $[26] = logoUrl;
      $[27] = name;
      $[28] = t7;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    t5 = "py-0.5 tab-bar-surface sticky top-below-nav z-10";
    t4 = "max-w-350 mx-auto px-4";
    t2 = "flex gap-2 my-2 overflow-x-auto scrollbar-hide";
    t3 = navLinks.map(t8 => {
      const {
        section: section_0,
        label
      } = t8;
      const isActive = activeSection === section_0;
      return <Link key={section_0 ?? "overview"} href={link(section_0)} prefetch={false} className={cn("px-5 py-3 font-medium text-xs uppercase whitespace-nowrap", isActive ? "btn-primary" : "link-themed")}>{label}</Link>;
    });
    $[4] = activeSection;
    $[5] = backgroundImageUrl;
    $[6] = breadcrumbs;
    $[7] = colors;
    $[8] = labels.competitions;
    $[9] = labels.matches.title;
    $[10] = labels.teamPage.tabs.news;
    $[11] = labels.teamPage.tabs.overview;
    $[12] = labels.teams.title;
    $[13] = link;
    $[14] = logoUrl;
    $[15] = name;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
  }
  let t7;
  if ($[30] !== t2 || $[31] !== t3) {
    t7 = <div className={t2}>{t3}</div>;
    $[30] = t2;
    $[31] = t3;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t4 || $[34] !== t7) {
    t8 = <div className={t4}>{t7}</div>;
    $[33] = t4;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== t5 || $[37] !== t8) {
    t9 = <nav className={t5}>{t8}</nav>;
    $[36] = t5;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== t6 || $[40] !== t9) {
    t10 = <div>{t6}{t9}</div>;
    $[39] = t6;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  return t10;
}